import React from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '../../../../theme/components/StatusPill';
import { StatusTone } from '../../../../theme/guideTokens';

// Pastillas de la revisión con IA: StatusPill de la guía ("Etiquetas de estado": borde, fondo tenue, MAYÚSCULAS bold).
// El estado nunca depende sólo del color: siempre lleva texto y, en alto contraste, el símbolo del tono.

interface PillProps {
  tone: StatusTone;
  text: string;
  title?: string;
  testId?: string;
}

export const Pill: React.FC<PillProps> = ({ tone, text, title, testId }) => (
  <StatusPill tone={tone} title={title} data-testid={testId}>
    {text}
  </StatusPill>
);

/** Texto i18n del valor; un código fuera de la lista muestra el código crudo con el respaldo "desconocido" (research R7). */
const useEnumText = (group: string) => {
  const { t } = useTranslation();
  return (value: string | null | undefined): string => {
    if (!value) return t('contractReview.enum.unknown', { value: '—' });
    const key = `contractReview.${group}.${value}`;
    const text = t(key);
    return text === key ? t('contractReview.enum.unknown', { value }) : text;
  };
};

const toneOf = (map: Record<string, StatusTone>, value: string | null | undefined): StatusTone => (value && map[value]) || 'neutral';

export const ReviewStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const text = useEnumText('status');
  const tone = toneOf(
    { Queued: 'neutral', Running: 'brand', Completed: 'info', Failed: 'danger', Applied: 'success', PartiallyApplied: 'warning', Reverted: 'neutral' },
    status
  );
  return <Pill tone={tone} text={text(status)} testId={`status-${status}`} />;
};

export const TriggerPill: React.FC<{ trigger: string }> = ({ trigger }) => {
  const text = useEnumText('trigger');
  return <Pill tone={trigger === 'AfterImport' ? 'confirmed' : 'neutral'} text={text(trigger)} testId={`trigger-${trigger}`} />;
};

export const ModePill: React.FC<{ mode: string }> = ({ mode }) => {
  const text = useEnumText('mode');
  return <Pill tone={mode === 'NewAddendum' ? 'confirmed' : 'neutral'} text={text(mode)} testId={`mode-${mode}`} />;
};

export const SeverityPill: React.FC<{ severity: string }> = ({ severity }) => {
  const text = useEnumText('severity');
  const tone = toneOf({ High: 'danger', Medium: 'warning', Low: 'info', Info: 'neutral' }, severity);
  return <Pill tone={tone} text={text(severity)} testId={`severity-${severity}`} />;
};

export const DecisionPill: React.FC<{ decision: string }> = ({ decision }) => {
  const text = useEnumText('decision');
  const tone = toneOf({ Accepted: 'success', Rejected: 'danger', Pending: 'warning' }, decision);
  return <Pill tone={tone} text={text(decision)} testId={`decision-${decision}`} />;
};

export const ApplyResultPill: React.FC<{ status: string | null | undefined }> = ({ status }) => {
  const text = useEnumText('applyStatus');
  const { t } = useTranslation();
  if (!status) return <Pill tone="neutral" text={t('contractReview.applyStatus.NotApplied')} testId="apply-NotApplied" />;
  const tone = toneOf({ Applied: 'success', Conflict: 'warning', Skipped: 'neutral', Failed: 'danger', Undone: 'neutral' }, status);
  return <Pill tone={tone} text={text(status)} testId={`apply-${status}`} />;
};

/** Contracts 021 §3.2: la decisión la tomó la IA al aplicar sola. */
export const AutoDecidedPill: React.FC = () => {
  const { t } = useTranslation();
  return <Pill tone="confirmed" text={t('contractReview.autoApply.pill')} title={t('contractReview.autoApply.pillTitle')} testId="auto-decided" />;
};

export const VerdictPill: React.FC<{ verdict: string }> = ({ verdict }) => {
  const text = useEnumText('verdict');
  const tone = toneOf({ Ok: 'success', Observations: 'warning', Incorrect: 'danger', NotInDocument: 'neutral' }, verdict);
  return <Pill tone={tone} text={text(verdict)} testId={`verdict-${verdict}`} />;
};

export const CheckStatusPill: React.FC<{ status: string }> = ({ status }) => {
  const text = useEnumText('checkStatus');
  const tone = toneOf({ Ok: 'success', Warning: 'warning', Error: 'danger', NotApplicable: 'neutral' }, status);
  return <Pill tone={tone} text={text(status)} testId={`check-${status}`} />;
};
